import { useMemo } from 'react';
import { entityRoomKeyFromEntityRef, usePerformControlWsStore } from '@/state/player/perform-control-ws.store';
import type { PerformControlV1 } from '@/types/player/perform-control-v1.types.ts';

/**
 * GET baseline ∪ Zustand overlay (same room as `live-context-perform-control` / `join-room`).
 * Item rows prefer WS patches merged on top of GET.
 *
 * Aggregate: normally `storeAggregate ?? GET` so WS can drive WAIT / etc. When GET already says
 * `phase: NONE` with no suggested command, we **do not** let a stale WS aggregate keep PRELOAD visible.
 */
export function useMergedPerformControl(
  entityRef: string,
  performControlFromGet: PerformControlV1 | undefined,
): PerformControlV1 | null {
  const roomKey = entityRoomKeyFromEntityRef(entityRef);

  const storeAggregate = usePerformControlWsStore((s) => (roomKey ? s.aggregateByRoom[roomKey] : undefined));
  const storeItems = usePerformControlWsStore((s) => (roomKey ? s.itemsByRoom[roomKey] : undefined));

  return useMemo(() => {
    if (!performControlFromGet || performControlFromGet.version !== 1) return null;
    if (!roomKey) return performControlFromGet;

    const hasStoreOverlay =
      storeAggregate !== undefined || (storeItems !== undefined && Object.keys(storeItems).length > 0);
    if (!hasStoreOverlay) {
      return performControlFromGet;
    }

    const getAgg = performControlFromGet.aggregate;
    const storeAgg = storeAggregate;
    /** GET says “no perform action” — do not let a stale WS aggregate keep PRELOAD/TRANSITION visible. */
    const getImpliesNoPerformAction = getAgg.phase === 'NONE' && getAgg.suggestedLayerCommand === undefined;
    const storeStillSuggestsPerform =
      storeAgg != null && (storeAgg.phase !== 'NONE' || storeAgg.suggestedLayerCommand !== undefined);
    const aggregate = getImpliesNoPerformAction && storeStillSuggestsPerform ? getAgg : (storeAgg ?? getAgg);

    return {
      version: 1 as const,
      computedAt: performControlFromGet.computedAt,
      aggregate,
      items: { ...performControlFromGet.items, ...(storeItems ?? {}) },
    };
  }, [roomKey, performControlFromGet, storeAggregate, storeItems]);
}
